import React, { Fragment } from 'react';
import styled from 'styled-components';
import { Row, Col } from 'react-flexbox-grid';
import { navigate, Link } from '@reach/router';
import { connect } from 'react-redux';
import { Button } from 'antd';
import { FormattedMessage } from 'react-intl';

import Layout from '../layouts/layout';
import Slider from '../components/home-slider';
import Wrapper from '../components/wrapper';
import Partners from '../components/partners';
import Certificate from '../components/home-certificate';
import ProductCard from '../components/product-card';
import HomeCustomer from '../components/home-customer';
import Loading from '../components/loading';
import ProductsSkeleton from '../components/products-skeleton';
import ArticleCard from '../components/article-card';
import { media } from '../utils/media';
import { useIsWidthLarger } from '../utils/useWindowSize';
import ProductCol from '../components/product-col';
import ProductLeftBanner from '../components/product-left-banner';

import { API_URL } from '../utils/service';
import { useQuery } from 'react-query';
import { getHome } from '../queries/page';

const SectionTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 40px 0px 20px 0px;
  color: #134E8F;
  ${media.phablet`
    font-size: 20px;
  `};
`;

const SectionSubtitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  padding: 10px 0px 20px 0px;
  color: #606060;
  ${media.phablet`
    font-size: 18px;
  `};
`;

const MoreWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
`;

const Banner = styled.img``

const IndexPage = ({ locale }) => {
  const { data, isFetching, error } = useQuery(['get-home'], () => getHome(), {
    select: data => data.data
  })

  const [isXL] = useIsWidthLarger([1200])

  if (error) return <p>{error}</p>;

  return (
    <Layout showSocial locale={locale}>
      <Slider
        loading={isFetching && !data?.logos}
        data={data?.logos ? data?.logos.filter(item => item.type === 2) : []}
        mobile={data?.logos ? data?.logos.filter(item => item.type === 5) : []}
      />
      <Wrapper style={{ minHeight: '100vh' }}>
        {!isFetching || data?.homeSections ? data?.homeSections.map(section => (
          <Fragment key={section.key}>
            <SectionTitle>{section.label}</SectionTitle>
            {section.items ? section.items.map(item => (
              <Fragment key={item.key}>
                <SectionSubtitle>{item.label}</SectionSubtitle>
                {item.banner && <Link to={item.banner.link || `/section/${item.key.replace('.', '-')}`} style={{marginBottom: 8}}>
                  <Banner src={`${API_URL}/upload/${item.banner.featuredImage}`} />
                </Link>}
                <div>
                  {item.bannerLeft && <ProductLeftBanner
                    src={`${API_URL}/upload/${item.bannerLeft.featuredImage}`}
                    href={item.bannerLeft.link || `/section/${item.key.replace('.', '-')}`}
                  />}
                  <Row style={{paddingLeft: 4, paddingRight: 4, marginTop: item.bannerLeft && isXL ? -688 : 0}}>
                    {
                      item.products.map((product, index) => {
                        return (
                          <Fragment key={product._id}>
                            {item.bannerLeft && isXL && (index === 0 || index === 5 || index === 10) && <ProductCol />}
                            <ProductCol key={product.name}>
                              <ProductCard locale={locale} data={product} />
                            </ProductCol>
                          </Fragment>
                        )
                      })
                    }
                  </Row>
                </div>
                {item.limit === item.products.length && (
                  <MoreWrapper>
                    <Button type='primary' onClick={() => navigate(`/section/${item.key.replace('.', '-')}`)}><FormattedMessage id='more' /></Button>
                  </MoreWrapper>
                )}
                {item.key === '1.1' && (
                    <Fragment>
                      <SectionTitle><FormattedMessage id='home.award' /></SectionTitle>
                      <div style={{ padding: '0px 20px' }}>
                        <Certificate data={data?.logos ? data?.logos.filter(item => item.type === 3) : []} />
                      </div>
                    </Fragment>
                )}
                {item.key === '1.4' && (
                  <Fragment>
                    <SectionTitle><FormattedMessage id='home.customer' /></SectionTitle>
                    <div style={{ marginBottom: 30 }}>
                      <Partners data={data?.logos ? data?.logos.filter(item => item.type === 4) : []} />
                    </div>
                  </Fragment>
                )}
              </Fragment>
            )) : (
              <Fragment>
                {section.banner && <Link to={section.banner.link || `/section/${section.key.replace('.', '-')}`} style={{marginBottom: 8}}>
                  <Banner src={`${API_URL}/upload/${section.banner.featuredImage}`} />
                </Link>}
                <div>
                  {section.bannerLeft && <ProductLeftBanner
                    src={`${API_URL}/upload/${section.bannerLeft.featuredImage}`}
                    href={section.bannerLeft.link || `/section/${section.key.replace('.', '-')}`}
                    style={{height: 608}}
                  />}
                  <Row style={{paddingLeft: 4, paddingRight: 4, marginTop: section.bannerLeft && isXL ? -616 : 0}}>
                    {
                      section.products.map((product, index) => {
                        return (
                          <>
                            {section.bannerLeft && (index === 0 || index === 5 || index === 10) && isXL && <ProductCol />}
                            <ProductCol key={product.name}>
                              <ProductCard locale={locale} data={product} />
                            </ProductCol>
                          </>
                        )
                      })
                    }
                  </Row>
                </div>
                {section.limit === section.products.length && (
                  <MoreWrapper>
                    <Button type='primary' onClick={() => navigate(`/section/${section.key.replace('.', '-')}`)}><FormattedMessage id='more' /></Button>
                  </MoreWrapper>
                )}
              </Fragment>
            )}
          </Fragment>
        )) : <ProductsSkeleton large />}
      </Wrapper>
      {isFetching || !data ? <Loading /> : (
        <Fragment>
          <Wrapper>
            <Row style={{ padding: 0, marginTop: 40 }}>
              {data?.news && data?.news.length > 0 && (
                <Col lg={6} md={12}>
                  <SectionTitle><FormattedMessage id='news.title' /></SectionTitle>
                  <Row>
                    {data?.news.map(item => (
                      <Col key={item._id} xs={12} md={6}>
                        <ArticleCard locale={locale} data={item} />
                      </Col>
                    ))}
                  </Row>
                  <MoreWrapper style={{ marginTop: 0 }}>
                    <Button type='primary' onClick={() => navigate('/news')}><FormattedMessage id='more' /></Button>
                  </MoreWrapper>
                </Col>
              )}
              {data?.advisories && data?.advisories.length > 0 && (
                <Col lg={6} md={12}>
                  <SectionTitle><FormattedMessage id='advisory.title' /></SectionTitle>
                  <Row>
                    {data?.advisories.map(item => (
                      <Col key={item._id} xs={12} md={6}>
                        <ArticleCard locale={locale} data={item} />
                      </Col>
                    ))}
                    <MoreWrapper style={{ marginTop: 0 }}>
                      <Button type='primary' onClick={() => navigate('/advisory')}><FormattedMessage id='more' /></Button>
                    </MoreWrapper>
                  </Row>
                </Col>
              )}
            </Row>
          </Wrapper>
          <HomeCustomer locale={locale} data={data?.reviews || []} />
        </Fragment>
      )}
    </Layout>
  )
}

export default connect(state => ({
  locale: state.setting.locale,
}))(IndexPage);
